/** Séparation Archives Facturation vs Inventaire vs Logistique — même table, filtre par source_type + type. */

export const ARCHIVE_MODULE_FACTURATION = 'facturation';
export const ARCHIVE_MODULE_INVENTAIRE = 'inventaire';
export const ARCHIVE_MODULE_LOGISTIQUE = 'logistique';

const INVENTAIRE_STOCK_TYPE_KEYS = [
  'reception_note',
  'reception-note',
  'receipt_note',
  'receipt-note',
  'stock_entry',
  'stock-entry',
  'exit_note',
  'exit-note',
  'exit_slip',
  'exit-slip',
  'outbound_slip',
  'outbound-slip',
  'transfer_note',
  'transfer-note',
  'transfer_slip',
  'transfer-slip',
];

const LOGISTIQUE_DOCUMENT_TYPE_KEYS = [
  'shipping',
  'shipping-slip',
  'shipping_slip',
];

/** @deprecated Use isInventaireStockDocType / isLogistiqueDocumentType — union of both modules. */
const INVENTORY_LOGISTICS_TYPE_KEYS = [
  ...INVENTAIRE_STOCK_TYPE_KEYS,
  ...LOGISTIQUE_DOCUMENT_TYPE_KEYS,
];

const INVENTAIRE_STOCK_TYPE_SET = new Set(
  INVENTAIRE_STOCK_TYPE_KEYS.flatMap((k) => [k, k.replace(/-/g, '_'), k.replace(/_/g, '-')]),
);

const LOGISTIQUE_TYPE_SET = new Set(
  LOGISTIQUE_DOCUMENT_TYPE_KEYS.flatMap((k) => [k, k.replace(/-/g, '_'), k.replace(/_/g, '-')]),
);

const INVENTORY_TYPE_SET = new Set(
  INVENTORY_LOGISTICS_TYPE_KEYS.flatMap((k) => [k, k.replace(/-/g, '_'), k.replace(/_/g, '-')]),
);

export function normalizeArchiveDocTypeKey(type) {
  return String(type ?? '')
    .trim()
    .replace(/-/g, '_')
    .toLowerCase();
}

function matchesTypeSet(type, typeSet) {
  const raw = String(type ?? '').trim();
  if (!raw) return false;
  if (typeSet.has(raw)) return true;
  if (typeSet.has(raw.replace(/-/g, '_'))) return true;
  if (typeSet.has(raw.replace(/_/g, '-'))) return true;
  return typeSet.has(normalizeArchiveDocTypeKey(raw));
}

/** Bon de réception, entrée, sortie, transfert — module Inventaire. */
export function isInventaireStockDocType(type) {
  return matchesTypeSet(type, INVENTAIRE_STOCK_TYPE_SET);
}

/** Bordereau d'envoi — module Logistique. */
export function isLogistiqueDocumentType(type) {
  return matchesTypeSet(type, LOGISTIQUE_TYPE_SET);
}

export function isInventoryLogisticsDocType(type) {
  return isInventaireStockDocType(type) || isLogistiqueDocumentType(type);
}

/** Canonical type → conversionMatrix key (ArchivesFacturation). */
const CONVERSION_MATRIX_KEY_BY_CANONICAL = {
  exit_note: 'exit_slip',
  exit_slip: 'exit_slip',
  outbound_slip: 'exit_slip',
  transfer_note: 'transfer_slip',
  transfer_slip: 'transfer_slip',
  reception_note: 'receipt_note',
  receipt_note: 'receipt_note',
  stock_entry: 'stock_entry',
  shipping: 'shipping_slip',
  shipping_slip: 'shipping_slip',
};

export function resolveConversionMatrixKey(type) {
  const raw = String(type ?? '').trim();
  if (!raw) return null;
  const canonical = normalizeArchiveDocTypeKey(raw);
  if (CONVERSION_MATRIX_KEY_BY_CANONICAL[canonical]) {
    return CONVERSION_MATRIX_KEY_BY_CANONICAL[canonical];
  }
  return canonical;
}

/** Module-scoped conversion targets (Achats / cross-module hidden in Inventaire & Logistique). */
export function filterConversionsForModule(conversions, moduleScope) {
  if (!Array.isArray(conversions)) return [];
  if (moduleScope === ARCHIVE_MODULE_INVENTAIRE) {
    return conversions.filter((c) => isInventaireStockDocType(c?.target));
  }
  if (moduleScope === ARCHIVE_MODULE_LOGISTIQUE) {
    return conversions.filter((c) => isLogistiqueDocumentType(c?.target));
  }
  return conversions;
}

/** Align converted doc type with DocumentCreation UI ids. */
export function normalizeInventoryDocTypeForUi(type) {
  const key = normalizeArchiveDocTypeKey(type);
  const uiMap = {
    reception_note: 'reception-note',
    receipt_note: 'reception-note',
    exit_note: 'exit-note',
    exit_slip: 'exit-note',
    outbound_slip: 'exit-note',
    transfer_note: 'transfer-note',
    transfer_slip: 'transfer-note',
    stock_entry: 'stock-entry',
    shipping: 'shipping-slip',
    shipping_slip: 'shipping-slip',
  };
  return uiMap[key] || type;
}

/** Module cible pour un type de document (création / conversion). */
export function getArchiveModuleForDocType(type) {
  if (isLogistiqueDocumentType(type)) return ARCHIVE_MODULE_LOGISTIQUE;
  if (isInventaireStockDocType(type)) return ARCHIVE_MODULE_INVENTAIRE;
  return ARCHIVE_MODULE_FACTURATION;
}

/** Module d’archivage effectif — source_type explicite ; sans tag → Facturation. */
export function resolveDocumentArchiveModule(doc) {
  if (!doc || typeof doc !== 'object') return ARCHIVE_MODULE_FACTURATION;
  const docType = doc.type;
  if (isLogistiqueDocumentType(docType)) return ARCHIVE_MODULE_LOGISTIQUE;
  if (isInventaireStockDocType(docType)) return ARCHIVE_MODULE_INVENTAIRE;
  const st = String(doc.sourceType ?? doc.source_type ?? '')
    .trim()
    .toLowerCase();
  if (st === ARCHIVE_MODULE_INVENTAIRE || st === 'inventory') return ARCHIVE_MODULE_INVENTAIRE;
  if (st === ARCHIVE_MODULE_LOGISTIQUE || st === 'logistics') return ARCHIVE_MODULE_LOGISTIQUE;
  if (st === ARCHIVE_MODULE_FACTURATION) return ARCHIVE_MODULE_FACTURATION;
  return ARCHIVE_MODULE_FACTURATION;
}

export function documentBelongsToArchiveModule(doc, moduleScope) {
  return resolveDocumentArchiveModule(doc) === moduleScope;
}

export function getArchivesRouteForModule(moduleScope) {
  if (moduleScope === ARCHIVE_MODULE_INVENTAIRE) return '/inventaire/archives';
  if (moduleScope === ARCHIVE_MODULE_LOGISTIQUE) return '/logistique/archives';
  return '/archives';
}

export function applyArchiveModuleToDocumentPayload(documentPayload, moduleScope) {
  if (!documentPayload || typeof documentPayload !== 'object') return documentPayload;
  let mod = ARCHIVE_MODULE_FACTURATION;
  if (moduleScope === ARCHIVE_MODULE_INVENTAIRE) {
    mod = ARCHIVE_MODULE_INVENTAIRE;
  } else if (moduleScope === ARCHIVE_MODULE_LOGISTIQUE) {
    mod = ARCHIVE_MODULE_LOGISTIQUE;
  } else if (typeof moduleScope === 'string' && moduleScope.trim()) {
    const st = moduleScope.trim().toLowerCase();
    if (st === ARCHIVE_MODULE_INVENTAIRE || st === 'inventory') {
      mod = ARCHIVE_MODULE_INVENTAIRE;
    } else if (st === ARCHIVE_MODULE_LOGISTIQUE || st === 'logistics') {
      mod = ARCHIVE_MODULE_LOGISTIQUE;
    }
  }
  documentPayload.sourceType = mod;
  documentPayload.source_type = mod;
  return documentPayload;
}
